// The clay-band clamp (THE SCHOOL-ACCENT LAW, portal only).
//
// A per-school accentColor may appear only in identity slots (crest, the 3px
// rule under the school name, the welcome line). Clay #E14E2C is reserved for
// status meaning (replied / booked / coached). If a school's brand accent hue
// sits in clay's band (~10–30°) with real chroma, an on-screen identity use
// would read as a status signal — so we render a shifted, still-branded safe
// variant instead.
//
// Pure functions, no deps. Hue band is [10, 30] degrees inclusive; achromatic /
// low-chroma inputs (grey) are left untouched.

const CLAY_BAND_MIN = 10;
const CLAY_BAND_MAX = 30;
// Below this HSL saturation an input is effectively grey — no hue collision.
const MIN_CHROMA_SAT = 0.15;
// Where a clamped hue is parked: a cool identity-safe hue well clear of clay.
const SAFE_HUE = 200;

function parseHex(hex) {
  if (typeof hex !== 'string') {
    return null;
  }
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h.split('').map((c) => { return c + c; }).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    return null;
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

// RGB (0–255) → HSL with h in [0,360) or null when achromatic.
function rgbToHsl({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) {
    return { h: null, s: 0, l };
  }

  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h;
  if (max === rn) {
    h = ((gn - bn) / delta) % 6;
  } else if (max === gn) {
    h = (bn - rn) / delta + 2;
  } else {
    h = (rn - gn) / delta + 4;
  }
  h *= 60;
  if (h < 0) {
    h += 360;
  }
  return { h, s, l };
}

function hslToRgb({ h, s, l }) {
  if (h === null || s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) { [r1, g1, b1] = [c, x, 0]; }
  else if (hp < 2) { [r1, g1, b1] = [x, c, 0]; }
  else if (hp < 3) { [r1, g1, b1] = [0, c, x]; }
  else if (hp < 4) { [r1, g1, b1] = [0, x, c]; }
  else if (hp < 5) { [r1, g1, b1] = [x, 0, c]; }
  else { [r1, g1, b1] = [c, 0, x]; }
  const m = l - c / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function rgbToHex({ r, g, b }) {
  const to2 = (v) => { return v.toString(16).padStart(2, '0'); };
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/**
 * True iff `hex` sits in the reserved clay hue band [10,30]° with non-trivial
 * chroma (an on-screen identity use would collide with clay's status meaning).
 * Grey / achromatic inputs are never in-band.
 * @param {string} hex
 * @returns {boolean}
 */
export function isInClayBand(hex) {
  const rgb = parseHex(hex);
  if (!rgb) {
    return false;
  }
  const { h, s } = rgbToHsl(rgb);
  if (h === null || s < MIN_CHROMA_SAT) {
    return false;
  }
  return h >= CLAY_BAND_MIN && h <= CLAY_BAND_MAX;
}

/**
 * Return an identity-safe accent for on-screen use. If `hex` is in the clay
 * band it is shifted to a cool safe hue (chroma/lightness preserved) so it
 * reads as identity, never status. Grey and out-of-band inputs are returned
 * unchanged (exact input string preserved).
 * @param {string} hex
 * @returns {string}
 */
export function clampAccent(hex) {
  if (!isInClayBand(hex)) {
    return hex;
  }
  const rgb = parseHex(hex);
  const hsl = rgbToHsl(rgb);
  const shifted = hslToRgb({ h: SAFE_HUE, s: hsl.s, l: hsl.l });
  return rgbToHex(shifted);
}
